import { DirectoryNameConsts } from 'client/util/Constants/DirectoryConstants';
import { listApplicationsByName } from 'client/applications/utils/ApplicationUtils';

/**
 * @see https://jumpcloud.atlassian.net/browse/HY-2324
 * This mixin is used in conjunction with `ConfiguredAppsDetectedModal.vue`, which uses the data
 * fields in this file to control the display of the modal, the data within the modal, and the
 * events that happen when the modal is "continued", and when it is closed.
 *
 * You will use the `checkForPreviouslyConfiguredAppsByName` function in the component of your
 * choice, supplying an appName (the app's type, not its displayName or displayLabel) and the
 * URL that redirects the user to the app's "configure" page (the page you go to when creating
 * a new application of that type), and then simply put `ConfiguredAppsDetectedModal` in the
 * template of that file.
 *
 * The rest of is taken care of here.
 *
 * For existing implementations, check:
 *  `HRDirectoriesEmptyState.vue`
 *  `DirectoriesItemTable.vue`
 */
export default {
  data: () => ({
    currentlySelectedAppConfigureURL: '',
    currentlySelectedAppDisplayName: '',
    previouslyConfiguredApps: [],
    showConfiguredAppsDetectedModal: false,
  }),

  methods: {
    /**
     * @param {string} appName app's "name" field - not the displayLabel/displayName,
     * but rather the type of the application. It should map to the "name" field in an
     * application's Mongo data.
     * e.g. Personio's "name" field is: `personio`
     *
     * @param {string} configureURL the URL that will take you to the app's "configure"
     * page.
     */
    async checkForPreviouslyConfiguredAppsByName({ appName, configureURL }) {
      const previouslyConfiguredApps = await listApplicationsByName(appName);

      if (previouslyConfiguredApps?.length) {
        this.previouslyConfiguredApps = previouslyConfiguredApps.map(app => ({
          displayLabel: app.displayLabel,
          logoUrl: app.logo.url,
          id: app.id,
        }));

        this.currentlySelectedAppConfigureURL = configureURL;
        this.currentlySelectedAppDisplayName = DirectoryNameConsts[appName];
        this.showConfiguredAppsDetectedModal = true;
      } else {
        this.$router.push(configureURL);
      }
    },

    /**
     * Resets the mixin data, with the exception of `showConfiguredAppsDetectedModal`.
     * This method is called on both close/cancel and continue.
     *
     * In either case, the intention is to close the modal first before proceeding with
     * the next action.
     *
     * For cancel, nothing happens after the modal closes, so we just call this method
     * after we close the modal.
     *
     * However, for continue (if the admin clicks the "Continue" button in the modal footer),
     * we want to close the modal, redirect the admin, and then reset the modal data. The mixin
     * data contains the ConfigureURL (the URL we're redirecting the admin to), so we need to
     * redirect first and then reset the data.
     */
    resetAppsDetectedModalData() {
      this.currentlySelectedAppConfigureURL = '';
      this.currentlySelectedAppDisplayName = '';
      this.previouslyConfiguredApps = [];
    },

    handleAppsDetectedModalClose() {
      this.showConfiguredAppsDetectedModal = false;

      this.resetAppsDetectedModalData();
    },

    handleAppsDetectedModalContinue() {
      this.showConfiguredAppsDetectedModal = false;
      this.$router.push(this.currentlySelectedAppConfigureURL);

      this.resetAppsDetectedModalData();
    },
  },
};
